import firebase from "firebase/app";
import {serializer} from "@/api/shared/serializer";
import {GalleryFolder} from "@/models/GalleryFolder";
import Loader from "@/components/loader/Loader.vue";
import {deleteFileByUrl, optimizePhotos, uploadImageAsync} from "@/utils/upload-image";
import ConfirmDlg from '@/components/popup/confirm-dialog/ConfirmDlg.vue';
import { v4 as uuidv4 } from "uuid";
import GalleryShareDlg from "@/views/staff/gallery/GalleryShareDlg/GalleryShareDlg.vue";
import SharePhotoDlg from "@/views/staff/gallery/SharePhotoDlg/SharePhotoDlg.vue";
import GalleryMovePhotosDlg from "@/views/staff/gallery/GalleryMovePhotosDlg/GalleryMovePhotosDlg.vue"
import {waitResizedImage} from "@/utils/wait-resized-image";
import { UserTypeEnum } from '@/enums/UserTypeEnum';
import { AuditLogsTier1 as Tier1, AuditLogsTier2 as Tier2, AuditLogsTier3 as Tier3 } from '@/enums/AuditLogs';

export default {
  name: "GalleryFolderView",
  components: {
    Loader,
    ConfirmDlg,
    GalleryShareDlg,
    SharePhotoDlg,
    GalleryMovePhotosDlg
  },
  data() {
    return {
      name: '',
      loading: true,
      imgLoading: false,
      selectedPhotos: [],
      addMediaDialog: false,
      currentFolder: [],
      photos: [],
      selectAll: false
    }
  },
  computed: {
    facility() {
      return this.$store.state.facilityModule.selectedFacility;
    },
    staffUser() {
      return this.$store.state.authModule.user;
    },
    folderPhotos() {
      return this.$store.state.galleryModule.galleryFolderPhotos;
    },
    hasSelectedPhotos(){
      return this.selectedPhotos?.length > 0
    }
  },
  async created() {
    await this.getCurrentGalleryFolder();
    await this.$store.dispatch("getGalleryFolderPhotos", this.$route.params.id)
    this.photos = this.folderPhotos.map(x => ({ ...x, isSelected: false }))
    this.loading = false;
  },
  methods: {
    async getCurrentGalleryFolder() {
      firebase
        .firestore()
        .collection("galleryFolders")
        .doc(this.$route.params.id)
        .get()
        .then((data) => {
          const folder: GalleryFolder = serializer.documentSnapshotToDocument(data);
          this.name = folder.name;
          this.currentFolder = folder
        })
    },
    async saveEventImage(e) {
      this.imgLoading = true;
      const files = e.target.files;

      if (files.length) {
        const uploadPromises = [];

        for (let i = 0; i < files.length; i++) {
          const file: any = await optimizePhotos(files[i], 'background-image');
          const path = `${this.facility.id}/photos/staff/${uuidv4()}`;

          const uploadPromise = uploadImageAsync({ path, file }).then(async (storage) => {
            const url = await storage.ref.getDownloadURL();
            const id = String(new Date().getTime());

            const resizedImageRef = await firebase.storage().ref().child(`${path}_200x200`);
            const resizedImageUrl = await waitResizedImage(10, resizedImageRef);

            const data = {
              createdAt: new Date().getTime(),
              facilityId: this.facility.id,
              image: url,
              thumbnailUrl: resizedImageUrl,
              isStaff: true,
              isVisibleForStaffGallery: false,
              sentBy: this.staffUser.name,
              sentById: this.staffUser.id,
              residentId: null,
              isUploaded: true,
              folderId: this.$route.params.id,
              createdById: this.staffUser.id,
              createdByType: UserTypeEnum.Staff,
              typesOfRecordChanged: [`${Tier1.ContentManager}: ${Tier2.Gallery}: ${Tier3.AddImage}`]
            };

            await firebase
              .firestore()
              .collection("photos")
              .doc(id)
              .set(data);

            return true;
          });

          uploadPromises.push(uploadPromise);
        }

        try {
          await Promise.all(uploadPromises).then(() => {
            this.getPhotos();
            this.$toast.open({
              message: files.length > 1 ? this.$t('Gallery.SuccessUploadingImages') : this.$t('Gallery.SuccessUploadingImage'),
              type: "success"
            });
            this.clearInputFile();
          })
        } catch (error) {
          console.error("Error uploading images:", error);
        } finally {
          this.imgLoading = false;
        }
      }
    },
    clearInputFile() {
      const fileInput = document.getElementById('fileInput') as HTMLInputElement;
      fileInput.value = "";
    },
    onPhotoSelect(isSelected, photo) {
    const idx = this.selectedPhotos.findIndex(x => x.id == photo.id);
    if (isSelected) {
      photo.isSelected = true; 
      if (idx === -1) this.selectedPhotos.push(photo);
    } else {
      photo.isSelected = false;
      if (idx !== -1) this.selectedPhotos.splice(idx, 1);
    }

    if (this.selectedPhotos.length === this.photos.length && this.photos.length > 0) {
      this.selectAll = true;
    } else {
      this.selectAll = false;
    }
  },
   onSelectAll(val){
      this.selectAll = !!val;
      this.photos.forEach(x => { x.isSelected = this.selectAll });
      this.selectedPhotos = this.selectAll ? [...this.photos] : []
    },
    openDialog(event, photo) {
      if (event.target.className.includes('selection-controls')) return;

      this.$refs.galleryShareDlg.open(photo);
    },
    openMovePhotosDialog(){
      this.$refs.galleryMovePhotosDlgRef.open(this.selectedPhotos, this.currentFolder);
    },
    async deletePhoto() {
      if (this.selectedPhotos.length <= 0) return

      this.$refs.confirmationModal
        .open(null,
          this.selectedPhotos.length > 1 ? this.$i18n.messages[this.$i18n.locale]['Gallery.AreYouSureYouWantToDeleteThesePicturesMessage'] :
            this.$i18n.messages[this.$i18n.locale]['Gallery.AreYouSureYouWantToDeleteThisPictureMessage'], false)
        .then(async (response) => {
          if (response && response.agree) {
            await this.deletePhotos(this.selectedPhotos)
          }
        });
    },
    async deletePhotos(photos) {
      this.loading = true;

      for (const photo of photos) {
        const photoRef = await firebase.firestore().collection("photos").doc(photo.id);

        await photoRef.update({
          deletedById: this.$store.state.authModule.user.id,
          deletedByType: UserTypeEnum.Staff,
          typesOfRecordChanged: [`${Tier1.ContentManager}: ${Tier2.Gallery}: ${Tier3.DeleteImage}`]
        });

        await photoRef
          .delete()
          .then(async () => {

            await deleteFileByUrl(photo.image)
              .then(async () => {
                const str = photo.image;
                const from = 0;
                const to = str.search('alt') - 1;
                const first = str.substring(from, to) + '_200x200';
                const second = str.substring(to, str.length);
                const url = first + second;
                await deleteFileByUrl(url);

                this.selectedPhotos = this.selectedPhotos.filter(x => x.id != photo.id)

                await this.$store.dispatch("getGalleryFolderPhotos", this.$route.params.id)
                this.photos = this.folderPhotos.map(x => ({ ...x, isSelected: false }))
              })
              .catch(() => {
                this.$toast.open({
                  message: this.$i18n.messages[this.$i18n.locale]['Gallery.PhotoCannotBeDeletedMessage'],
                  type: 'error',
                });
              });
          })
          .catch(() => {
            this.$toast.open({
              message: this.$i18n.messages[this.$i18n.locale]['Gallery.PhotoCannotBeDeletedMessage'],
              type: 'error',
            });
          });
      }

      this.loading = false;
      this.addMediaDialog = false;

      this.$toast.open({
        message: photos.length > 1? this.$i18n.messages[this.$i18n.locale]['Gallery.PhotosSuccessfullyDeleted'] : this.$i18n.messages[this.$i18n.locale]['Gallery.PhotoSuccessfullyDeleted'],
        type: 'success',
      });

    },
    async getPhotos(photo) {
      this.selectedPhotos = this.selectedPhotos.filter(x => x.id != photo?.id);

      await this.$store.dispatch("getGalleryFolderPhotos", this.$route.params.id)
      this.photos = this.folderPhotos.map(x => ({ ...x, isSelected: false }))
      this.selectAll = false;
    },
    sharePhotoByEmail(photo) {
      this.$refs.sharePhotoDlg.open(photo);
    },
    async deleteFolder() {
      const galleryRef =  await firebase.firestore().collection("galleryFolders").doc(this.$route.params.id);

      await galleryRef.update({
        deletedById: this.$store.state.authModule.user.id,
        deletedByType: UserTypeEnum.Staff,
        typesOfRecordChanged: [`${Tier1.ContentManager}: ${Tier2.Gallery}: ${Tier3.DeleteFolder}`]
      })

      await galleryRef.delete()
        .then(() => {
          this.$toast.open({
            message: this.$t("Gallery.FolderHasBeenDeleted"),
            type: "success"
          });

          this.$router.push("/gallery");
        }).catch((error) => {
        this.$toast.open({
          message: this.$t("Gallery.ErrorFolderDeleting"),
          type: "error"
        })
      })
    },
    onImagesMoved(movedPhotosIds){
      this.photos = this.photos.filter(x=> !movedPhotosIds.includes(x.id))
      this.selectedPhotos = []
      this.selectAll = false
    }
  }
}
